import { useCallback, useContext, useEffect, useMemo } from 'react';
import get from 'lodash.get';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';

import { ContestManageContext } from '../../common/context/ContestManageContext';
import type { FieldValue } from '../../common/types/FieldValue';

export const useFieldValue = <V extends FieldValue>(
  sectionKey: string,
  path: string[],
  defaultValue?: V,
  isVisible?: boolean
) => {
  const { values, setValues, errors, touched, setTouched } = useContext(ContestManageContext);

  const handleSetValue = useCallback(
    (newValueOrCb: V | ((prevValue: V) => V)) => {
      setValues((prev) => {
        const prevValue = get(prev, [sectionKey, ...path]) as V;
        const newValue =
          typeof newValueOrCb === 'function' ? newValueOrCb(prevValue) : newValueOrCb;

        return set(cloneDeep(prev), [sectionKey, ...path], newValue);
      });
    },
    [path, sectionKey, setValues]
  );

  const fieldValue = useMemo(() => get(values, [sectionKey, ...path]), [path, sectionKey, values]);

  const fieldErrors = useMemo(
    () => get(errors, [sectionKey, ...path]) as string[] | undefined,
    [path, sectionKey, errors]
  );

  const fieldIsTouched = useMemo(
    () => get(touched, [sectionKey, ...path]) as boolean | undefined,
    [path, sectionKey, touched]
  );

  const onValueChange = useCallback(
    (newValueOrCb: V | ((prevValue: V) => V), skipTouched?: boolean) => {
      handleSetValue(newValueOrCb);
      if (!skipTouched) {
        setTouched((prev) => set(prev, [sectionKey, ...path], true));
      }
    },
    [handleSetValue, path, sectionKey, setTouched]
  );

  const onPreserveSections = useCallback(
    (keys: string[]) => {
      setValues((prev) => keys.reduce((acc, key) => ({ ...acc, [key]: prev[key] }), {}));
      setTouched((prev) => keys.reduce((acc, key) => ({ ...acc, [key]: prev[key] }), {}));
    },
    [setValues, setTouched]
  );

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    if (typeof defaultValue === 'undefined') {
      return;
    }
    if (typeof fieldValue !== 'undefined') {
      return;
    }
    if (fieldIsTouched) {
      return;
    }

    onValueChange(defaultValue, true);
  }, [defaultValue, fieldIsTouched, fieldValue, isVisible, onValueChange]);

  return {
    value: fieldValue,
    onValueChange,
    errors: fieldErrors,
    isTouched: fieldIsTouched,
    onPreserveSections,
  };
};
